import {
  CloudUploadOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  SearchOutlined
} from "@ant-design/icons"
import { Alert, Button, Segmented, Select, Skeleton, Tag } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { TextInput } from "../../components/Input/TextInput.tsx"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { downloadFile } from "../../utils/download-file.ts"
import { PortalEmptyState } from "./components/PortalEmptyState.tsx"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { ProjectContextBanner } from "./components/ProjectContextBanner.tsx"
import {
  UploadAndShareModal,
  type UploadProjectOption
} from "./components/UploadAndShareModal.tsx"
import { resolveRecipientLabel } from "./helpers/recipient.ts"
import { formatPortalDate } from "./helpers/format-date.ts"
import {
  fetchAllProjectsDocuments,
  fetchPortalDocuments,
  uploadDocumentThunk
} from "./redux/portal.thunks.ts"
import type {
  PortalCrossProjectDocument,
  PortalDocument
} from "./types/portal.types.ts"

type SourceFilter = "all" | "contractor" | "you"

/** The global, cross-project Documents directory — `/portal/documents`. A
 * plain sidebar visit shows every authorized project; `?project=<id>` (set
 * by Project Overview's "View documents", an old project-tab deep link, or
 * typed directly) pre-scopes it to one, shown via `ProjectContextBanner`
 * rather than a hidden selection — reading straight from the URL is what
 * keeps this correct across a refresh, browser back, or a bookmark. */
function PortalDocumentsPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<string | null>(null)
  const [source, setSource] = useState<SourceFilter>("all")
  const [projectFilter, setProjectFilter] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(
    searchParams.get("upload") === "1"
  )

  const userInfo = useAppSelector((state) => state.auth.userInfo)
  const selectedProjectId = searchParams.get("project")
  const projects = useAppSelector((state) => state.portal.projects)
  const projectsLoaded = useAppSelector((state) => state.portal.projectsLoaded)
  const detail = useAppSelector((state) => state.portal.projectDetail)
  const documents = useAppSelector((state) => state.portal.documents)
  const fetching = useAppSelector((state) => state.portal.documentsFetching)
  const loaded = useAppSelector((state) => state.portal.documentsLoaded)
  const failed = useAppSelector((state) => state.portal.documentsError)
  const crossDocuments = useAppSelector(
    (state) => state.portal.crossProjectDocuments
  )
  const crossFetching = useAppSelector(
    (state) => state.portal.crossProjectDocumentsFetching
  )
  const crossLoaded = useAppSelector(
    (state) => state.portal.crossProjectDocumentsLoaded
  )

  const allProjectsMode = selectedProjectId === null

  useEffect(() => {
    if (selectedProjectId)
      void dispatch(fetchPortalDocuments(selectedProjectId))
  }, [dispatch, selectedProjectId])

  // A direct visit/refresh in All-projects mode can land here before the
  // sidebar's own fetch (PortalLayout) has run — make sure the aggregate is
  // requested from here too rather than showing an empty page forever.
  useEffect(() => {
    if (
      allProjectsMode &&
      projectsLoaded &&
      projects.length > 0 &&
      !crossLoaded &&
      !crossFetching
    ) {
      void dispatch(
        fetchAllProjectsDocuments(
          projects.map((p) => ({ id: p.id, lead_name: p.lead_name }))
        )
      )
    }
  }, [
    allProjectsMode,
    projectsLoaded,
    projects,
    crossLoaded,
    crossFetching,
    dispatch
  ])

  useEffect(() => {
    if (searchParams.get("upload") === "1") {
      setUploadOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete("upload")
      setSearchParams(next, { replace: true })
    }
  }, [])

  const projectName =
    projects.find((p) => p.id === selectedProjectId)?.lead_name ?? ""
  const recipientLabel = resolveRecipientLabel(detail?.project_managers, t)

  const uploadProjectOptions: UploadProjectOption[] = useMemo(
    () =>
      projects.map((project) => ({
        id: project.id,
        name: project.lead_name,
        recipientLabel: resolveRecipientLabel(project.project_managers, t)
      })),
    [projects, t]
  )

  const sourceRows: (PortalDocument | PortalCrossProjectDocument)[] =
    allProjectsMode ? crossDocuments : documents

  const projectOptionsForFilter = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.lead_name })),
    [projects]
  )

  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>()
    sourceRows.forEach((doc) => {
      const label = doc.document_type_label
      if (label) seen.set(label, label)
    })
    return Array.from(seen.values()).map((label) => ({ value: label, label }))
  }, [sourceRows])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return sourceRows.filter((doc) => {
      const matchesSearch =
        !query || doc.file_name.toLowerCase().includes(query)
      const matchesType = !typeFilter || doc.document_type_label === typeFilter
      const matchesProject =
        !allProjectsMode ||
        !projectFilter ||
        (doc as PortalCrossProjectDocument).project_id === projectFilter
      const isMine = doc.uploaded_by_id === userInfo?.id
      const matchesSource =
        source === "all" ||
        (source === "you" && isMine) ||
        (source === "contractor" && !isMine)
      return matchesSearch && matchesType && matchesSource && matchesProject
    })
  }, [
    sourceRows,
    search,
    typeFilter,
    source,
    userInfo?.id,
    allProjectsMode,
    projectFilter
  ])

  const handleDownload = (doc: PortalDocument) => {
    void downloadFile(doc.file, doc.file_name)
  }

  const handleUpload = async (file: File, note: string, projectId: string) => {
    const result = await dispatch(
      uploadDocumentThunk({ projectId, file, note })
    )
    return uploadDocumentThunk.fulfilled.match(result)
  }

  const handleAllUploaded = () => {
    if (selectedProjectId) {
      void dispatch(fetchPortalDocuments(selectedProjectId))
    } else {
      void dispatch(
        fetchAllProjectsDocuments(
          projects.map((p) => ({ id: p.id, lead_name: p.lead_name }))
        )
      )
    }
  }

  const isLoading = allProjectsMode
    ? crossFetching && !crossLoaded
    : fetching && !loaded
  const hasFailed = allProjectsMode ? false : failed
  const hasLoaded = allProjectsMode ? crossLoaded : loaded
  const anyHasType = sourceRows.some((doc) => doc.document_type_label)
  const anyHasStatus = sourceRows.some((doc) => doc.status_label)

  if (!projectsLoaded) {
    return (
      <PortalPageContainer>
        <Skeleton active paragraph={{ rows: 5 }} />
      </PortalPageContainer>
    )
  }

  if (projects.length === 0) {
    return (
      <PortalPageContainer>
        <PortalPageHeader title={t("portal.documents.title")} />
        <PortalEmptyState
          title={t("portal.home.noProjectsTitle")}
          description={t("portal.home.noProjectsDescription")}
        />
      </PortalPageContainer>
    )
  }

  return (
    <PortalPageContainer>
      <PortalPageHeader
        title={t("portal.documents.title")}
        actions={
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => setUploadOpen(true)}
          >
            {t("portal.upload.buttonLabel")}
          </Button>
        }
      />

      {selectedProjectId && (
        <ProjectContextBanner
          projectId={selectedProjectId}
          projectName={projectName}
          clearHref={RoutesPaths.portal.documents}
        />
      )}

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Segmented
          value={source}
          onChange={(value) => setSource(value as SourceFilter)}
          options={[
            { label: t("portal.documents.sourceAll"), value: "all" },
            {
              label: t("portal.documents.sourceContractor"),
              value: "contractor"
            },
            { label: t("portal.documents.sourceYou"), value: "you" }
          ]}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <TextInput
            allowClear
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder={t("portal.documents.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="sm:max-w-xs"
          />
          {allProjectsMode && (
            <Select
              allowClear
              placeholder={t("portal.documents.projectFilterPlaceholder")}
              value={projectFilter ?? undefined}
              onChange={(value) => setProjectFilter(value ?? null)}
              options={projectOptionsForFilter}
              className="sm:w-52"
            />
          )}
          <Select
            allowClear
            placeholder={t("portal.documents.typeFilterPlaceholder")}
            value={typeFilter ?? undefined}
            onChange={(value) => setTypeFilter(value ?? null)}
            options={typeOptions}
            className="sm:w-56"
          />
        </div>
      </div>

      {isLoading && <Skeleton active paragraph={{ rows: 5 }} />}

      {hasFailed && (
        <Alert
          type="error"
          showIcon
          message={t("portal.documents.loadError")}
          action={
            selectedProjectId && (
              <Button
                size="small"
                onClick={() =>
                  void dispatch(fetchPortalDocuments(selectedProjectId))
                }
              >
                {t("portal.home.retry")}
              </Button>
            )
          }
        />
      )}

      {hasLoaded && !hasFailed && sourceRows.length === 0 && (
        <PortalEmptyState
          title={t("portal.documents.emptyTitle")}
          description={t("portal.documents.emptyDescription")}
        />
      )}

      {hasLoaded &&
        !hasFailed &&
        sourceRows.length > 0 &&
        filtered.length === 0 && (
          <PortalEmptyState title={t("portal.documents.noMatches")} />
        )}

      {filtered.length > 0 && (
        <>
          {/* Desktop: a table. Below `md`, the same rule the rest of this
              app already uses for wide tables (docs/kb/decisions/
              2026-08-05-widget-tables-as-cards-below-md.md in ezbuild_front)
              — a horizontally-scrollable table on a narrow screen hides
              Version/Actions off-edge with no affordance, so it becomes a
              stacked card list instead. */}
          <div className="hidden overflow-x-auto rounded-lg border border-gray-100 bg-white md:block">
            <table className="w-full min-w-[680px] text-left text-sm">
              <thead className="border-b border-gray-100 text-xs uppercase text-gray-400">
                <tr>
                  <th className="px-4 py-3 font-medium">
                    {t("portal.documents.columnName")}
                  </th>
                  {allProjectsMode && (
                    <th className="px-4 py-3 font-medium">
                      {t("portal.documents.columnProject")}
                    </th>
                  )}
                  {anyHasType && (
                    <th className="px-4 py-3 font-medium">
                      {t("portal.documents.columnType")}
                    </th>
                  )}
                  {anyHasStatus && (
                    <th className="px-4 py-3 font-medium">
                      {t("portal.documents.columnStatus")}
                    </th>
                  )}
                  <th className="px-4 py-3 font-medium">
                    {t("portal.documents.columnSharedBy")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("portal.documents.columnShared")}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t("portal.documents.columnActions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc) => {
                  const isMine = doc.uploaded_by_id === userInfo?.id
                  const crossDoc = allProjectsMode
                    ? (doc as PortalCrossProjectDocument)
                    : null
                  return (
                    <tr
                      key={doc.id}
                      className="border-b border-gray-50 last:border-0"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-gray-800">
                          <FileTextOutlined className="text-gray-400" />
                          <span className="max-w-[220px] truncate">
                            {doc.file_name}
                          </span>
                          {doc.version > 1 && (
                            <span className="text-xs text-gray-400">
                              v{doc.version}
                            </span>
                          )}
                        </div>
                        {doc.folder_path.length > 0 && (
                          <p className="mt-0.5 truncate text-xs text-gray-400">
                            {doc.folder_path.map((f) => f.name).join(" / ")}
                          </p>
                        )}
                      </td>
                      {allProjectsMode && (
                        <td className="px-4 py-3 text-gray-500">
                          {crossDoc?.project_name}
                        </td>
                      )}
                      {anyHasType && (
                        <td className="px-4 py-3">
                          {doc.document_type_label ? (
                            <Tag>{doc.document_type_label}</Tag>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      {anyHasStatus && (
                        <td className="px-4 py-3">
                          {doc.status_label ? (
                            <Tag color="blue">{doc.status_label}</Tag>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 text-gray-500">
                        {isMine
                          ? t("portal.documents.sourceYou")
                          : (doc.uploaded_by_name ?? doc.uploaded_by ?? "—")}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {formatPortalDate(
                          doc.shared_with_customer_at ?? doc.created
                        ) ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            href={doc.file}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={t("portal.documents.view")}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<DownloadOutlined />}
                            onClick={() => handleDownload(doc)}
                            aria-label={t("portal.documents.download")}
                          />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 md:hidden">
            {filtered.map((doc) => {
              const isMine = doc.uploaded_by_id === userInfo?.id
              const crossDoc = allProjectsMode
                ? (doc as PortalCrossProjectDocument)
                : null
              return (
                <CardComponent key={doc.id} size="small">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2">
                      <FileTextOutlined className="mt-0.5 shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-gray-800">
                          {doc.file_name}
                        </p>
                        {crossDoc && (
                          <p className="text-xs font-medium text-primary">
                            {crossDoc.project_name}
                          </p>
                        )}
                        {doc.folder_path.length > 0 && (
                          <p className="truncate text-xs text-gray-400">
                            {doc.folder_path.map((f) => f.name).join(" / ")}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                          {doc.document_type_label && (
                            <Tag className="m-0">{doc.document_type_label}</Tag>
                          )}
                          {doc.status_label && (
                            <Tag className="m-0" color="blue">
                              {doc.status_label}
                            </Tag>
                          )}
                          <span>
                            {isMine
                              ? t("portal.documents.sourceYou")
                              : (doc.uploaded_by_name ?? doc.uploaded_by)}
                          </span>
                          <span>
                            {formatPortalDate(
                              doc.shared_with_customer_at ?? doc.created
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        href={doc.file}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t("portal.documents.view")}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownload(doc)}
                        aria-label={t("portal.documents.download")}
                      />
                    </div>
                  </div>
                </CardComponent>
              )
            })}
          </div>
        </>
      )}

      <UploadAndShareModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
        onAllUploaded={handleAllUploaded}
        {...(allProjectsMode
          ? { projectOptions: uploadProjectOptions }
          : {
              project: {
                id: selectedProjectId as string,
                name: projectName,
                recipientLabel
              }
            })}
      />
    </PortalPageContainer>
  )
}

export default PortalDocumentsPage
